import SearchBar from '-/Custom/SearchBar';
import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';
import * as PersonListItem from '@/components/Person/PersonListItem';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import List from '@/components/Unistyles/List';
import { graphql, readFragment } from '@/gql/graphql';
import type { PersonBoolExp } from '@/gql/scalars';
import useGenerateDm from '@/hooks/useGenerateDm';
import useSearchablePaginatedQuery from '@/hooks/useSearchablePaginatedQuery';
import useUserId from '@/hooks/useUserId';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';

export default function ChatCreateDirect() {
  const userId = useUserId();
  const [search, setSearch] = useState('');
  const { dismissTo } = useRouter();
  const upsertDm = useGenerateDm();

  const GetPeople = graphql(/* GraphQL */ `
    query GetChatCreateDirectPeople(
      $where: person_bool_exp!
      $limit: Int!
      $offset: Int!
    ) {
      person(
        where: $where
        limit: $limit
        offset: $offset
        order_by: { name: asc }
      ) {
        id
        username
        ...PersonListItemAvatar
        ...PersonListItemName
      }
    }
  `, [
    PersonListItem.PersonListItemAvatarFragment,
    PersonListItem.PersonListItemNameFragment,
  ]);

  const SearchPeople = graphql(/* GraphQL */ `
    query SearchChatCreateDirectPeople(
      $where: person_bool_exp!
      $limit: Int!
      $offset: Int!
    ) {
      person(
        where: $where
        limit: $limit
        offset: $offset
        order_by: { name: asc }
      ) {
        id
        username
        ...PersonListItemAvatar
        ...PersonListItemName
      }
    }
  `, [
    PersonListItem.PersonListItemAvatarFragment,
    PersonListItem.PersonListItemNameFragment,
  ]);

  const baseWhere: PersonBoolExp = {
    _and: [
      { contact_access: { viewer_person_id: { _eq: userId } } },
      { is_support: { _eq: false } },
    ],
  };

  const searchWhere: PersonBoolExp = {
    _and: [
      baseWhere,
      {
        name: {
          _ilike: `%${search}%`,
        },
      },
    ],
  };

  const { items, fetching } = useSearchablePaginatedQuery({
    searchTerm: search,
    searchOnMinimum: DEFAULT_SEARCH_MINIMUM,
    pageSize: 25,
    getQuery: GetPeople,
    getVariables: ({ limit, offset }) => ({
      where: baseWhere,
      limit,
      offset,
    }),
    selectGetItems: (d) => d?.person ?? [],
    searchQuery: SearchPeople,
    searchVariables: ({ searchTerm }) => ({
      where:
        searchTerm.length >= DEFAULT_SEARCH_MINIMUM ? searchWhere : baseWhere,
      limit: 25,
      offset: 0,
    }),
    selectSearchItems: (d) => d?.person ?? [],
  });

  return (
    <>
      <Stack.Screen options={{ title: 'New Direct Message' }} />
      <Box px="xl" py="sm">
        <SearchBar value={search} onChangeText={setSearch} />
      </Box>
      <List
        data={items}
        fetching={fetching}
        renderItem={({ item }) => {
          const name = readFragment(
            PersonListItem.PersonListItemNameFragment,
            item,
          );

          return (
            <Item
              aria-label={name.name || 'Unknown User'}
              onPress={async () => {
                const id = await upsertDm(item.id);
                if (id) {
                  dismissTo({
                    pathname: '/chat/[chatId]',
                    params: { chatId: id },
                  });
                }
              }}
            >
              <PersonListItem.Container>
                <PersonListItem.Avatar fragment={item} />
                <PersonListItem.Content>
                  <PersonListItem.Name fragment={item} />
                </PersonListItem.Content>
              </PersonListItem.Container>
            </Item>
          );
        }}
      />
    </>
  );
}
